<script setup lang="ts">
import ChevronDownIcon from '@/assets/icons/ChevronDownIcon.vue';
import { getSubjectTintStyle } from '@/utils/color.utils';
import { computed } from 'vue';

export type TComposerChipAppearance = 'accent' | 'default' | 'ghost' | 'neutral';

const props = withDefaults(
  defineProps<{
    label: string;
    appearance?: TComposerChipAppearance;
    tintColor?: string;
    accessibleLabel?: string;
    disabled?: boolean;
    expanded?: boolean;
    chevron?: boolean;
    readOnly?: boolean;
  }>(),
  {
    appearance: 'default',
    tintColor: undefined,
    accessibleLabel: undefined,
    disabled: false,
    expanded: false,
    chevron: false,
    readOnly: false,
  },
);

const emit = defineEmits<{ click: [anchor: HTMLElement] }>();

const appearanceClass = computed<string>(() => {
  if (props.expanded) {
    return 'border-transparent bg-highlight text-tint';
  }
  switch (props.appearance) {
    case 'accent':
      return 'border-transparent bg-surface-tertiary text-tint';
    case 'ghost':
      return 'border-dashed border-quaternary bg-surface-primary text-secondary';
    case 'neutral':
      return 'border-transparent bg-surface-tertiary text-primary';
    default:
      return 'border-transparent bg-surface-primary text-secondary';
  }
});

const tintStyle = computed<Record<string, string> | undefined>(() => getSubjectTintStyle(props.tintColor));

const handleClick = (event: Event): void => {
  if (props.readOnly || !(event.currentTarget instanceof HTMLElement)) return;
  emit('click', event.currentTarget);
};
</script>

<template>
  <component
    :is="props.readOnly ? 'span' : 'button'"
    :type="props.readOnly ? undefined : 'button'"
    class="chip border"
    :class="[appearanceClass]"
    :style="tintStyle"
    :disabled="props.readOnly ? undefined : props.disabled"
    :aria-haspopup="props.readOnly ? undefined : 'dialog'"
    :aria-expanded="props.readOnly ? undefined : props.expanded"
    :aria-label="props.accessibleLabel"
    @click="handleClick"
  >
    <slot name="icon" />
    <span class="max-w-48 truncate">{{ props.label }}</span>
    <ChevronDownIcon
      v-if="props.chevron && !props.readOnly"
      class="-ml-sm size-icon-xs shrink-0 text-secondary"
      aria-hidden="true"
    />
  </component>
</template>
